import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Lock, Plus } from "lucide-react";

const LBL = "font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-1 block";
const SEL = "w-full rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm";

export function DocToKbDialog({ doc, getHtml, onClose, onDone }) {
  const [tree, setTree] = useState(null);
  const [title, setTitle] = useState(doc.name || "");
  const [gid, setGid] = useState("");
  const [cid, setCid] = useState("");
  const [sid, setSid] = useState("");
  const [newCat, setNewCat] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/console/kb/tree").then(({ data }) => setTree(data.groups || [])).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); setTree([]); });
  useEffect(() => { load(); }, []);
  const group = (tree || []).find((g) => g.id === gid);
  const cat = group?.categories.find((c) => c.id === cid);
  const needsSub = !!cat?.subcategories.length;
  const target = needsSub ? sid : cid;
  const createCat = async () => {
    try {
      const { data } = await api.post("/console/kb/categories", { group_id: gid, name: newCat.trim() });
      await load(); setCid(data.id); setSid(""); setNewCat(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const convert = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/console/docs/${doc.id}/to-kb`, { category_id: target, title, body: getHtml() });
      toast.success("Knowledge Base article created"); onDone(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-[#EEEFEF] max-w-md" data-testid="doc-kb-dialog">
        <DialogHeader><DialogTitle className="font-display">Convert to Knowledge Base article</DialogTitle><DialogDescription className="text-[#9FA1A4]">Copies this doc's current content into a new article. Later edits to the doc won't change the article.</DialogDescription></DialogHeader>
        {!tree ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div> : (
          <div className="space-y-3">
            <div><label className={LBL}>Article title</label><Input data-testid="doc-kb-title" value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" /></div>
            <div><label className={LBL}>Group</label>
              <select data-testid="doc-kb-group" value={gid} onChange={(e) => { setGid(e.target.value); setCid(""); setSid(""); setNewCat(null); }} className={SEL}>
                <option value="">Choose a group…</option>{tree.map((g) => <option key={g.id} value={g.id}>{g.name}{g.internal ? " (internal)" : ""}</option>)}
              </select></div>
            {group && (newCat === null ? (
              <div><div className="flex items-center justify-between"><label className={LBL}>Category</label><button data-testid="doc-kb-new-category" onClick={() => setNewCat("")} className="text-[11px] text-[#723FFF] hover:underline flex items-center gap-0.5 mb-1"><Plus className="h-3 w-3" />New category</button></div>
                <select data-testid="doc-kb-category" value={cid} onChange={(e) => { setCid(e.target.value); setSid(""); }} className={SEL}>
                  <option value="">Choose a category…</option>{group.categories.map((c) => <option key={c.id} value={c.id}>{c.name}{c.subcategories.length ? ` (${c.subcategories.length} sub-categories)` : ""}</option>)}
                </select></div>
            ) : (
              <div><label className={LBL}>New category in {group.name}</label>
                <div className="flex gap-2"><Input data-testid="doc-kb-new-category-name" autoFocus value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Category name" className="bg-[#F6F6FD] border-[#EEEFEF]" />
                  <Button data-testid="doc-kb-new-category-save" size="sm" onClick={createCat} disabled={!newCat.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Add</Button>
                  <Button size="sm" variant="ghost" onClick={() => setNewCat(null)}>Cancel</Button></div></div>
            ))}
            {needsSub && (
              <div><label className={LBL}>Sub-category</label>
                <select data-testid="doc-kb-subcategory" value={sid} onChange={(e) => setSid(e.target.value)} className={SEL}>
                  <option value="">Choose a sub-category…</option>{cat.subcategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            )}
            {group?.internal && <p className="text-[11px] text-[#9FA1A4] flex items-center gap-1"><Lock className="h-3 w-3" /> Internal group: only agency staff will see it.</p>}
          </div>
        )}
        <DialogFooter><Button data-testid="doc-kb-convert" onClick={convert} disabled={saving || !target || !title.trim()} className="rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create article"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
